<script>
  // Step 1 body: Set Page Scale. Renders inside a <StepCard>; the parent
  // panel owns the card's number/title/subline/done/warning.
  import { createEventDispatcher, onDestroy } from "svelte";

  import { Modal } from "svelte-utilities";
  import SelectInput from "#src/lib/sidebar/SelectInput.svelte";
  import CustomScaleModal from "#src/lib/sidebar/CustomScaleModal.svelte";
  import { scaleOptions } from "@local/extensions/utilities/scales.js";
  import pageScaleUpdates from "#src/extensions/page-scale-updates.js";
  import { currentTool } from "#src/stores/ui.js";
  import { isNotApplicable, isScaled } from "../derive.js";
  import { plural } from "../labels.js";
  import { setPageFlags } from "../page-flags.js";
  import { forceScaleFor } from "../force-scale.js";

  export let group;
  export let doc;
  export let pages;
  export let indexedLocations;
  export let selectedPageList;

  const dispatch = createEventDispatcher();

  let scaleModal;
  let customScaleModal;
  let scaleValue = null;
  let overrideExisting = true;
  let forcedNote = "";
  let consumedForcePageId = null;
  // True while the scale modal is open because a frame placement was blocked
  // on an unscaled page — set below, used to re-arm the frame tool on confirm.
  let forced = false;
  let pendingForcedOpen = false;

  // Something needs this page scaled before it can proceed: a frame placement
  // (Document.svelte's shpx+framex-gated hook, which aborts before any frame is
  // created) or Find Frames pressed with an unscaled page selected. Auto-open the
  // same modal the "Set page scale manually" link opens, with a note explaining
  // why. Keyed on pageId so re-renders don't reopen a modal the user already
  // dismissed; the Modal's on:close clears that key, so a later press can reopen it.
  $: if ($forceScaleFor) {
    const { pageId, note } = $forceScaleFor;
    if (pageId !== consumedForcePageId) {
      consumedForcePageId = pageId;
      forced = true;
      // Placement is the default wording; other callers pass their own.
      forcedNote = note ?? "This page needs a scale before you can place a frame on it.";
      forceScaleFor.set(null);
      pendingForcedOpen = true;
    }
  }

  // The block above can run during Step 1's initial mount (the forced flow
  // expands Step 1, mounting this component), before the Modal's bind:this
  // has set `scaleModal`. Defer the open until scaleModal exists so we never
  // call .open() on undefined.
  $: if (pendingForcedOpen && scaleModal) {
    pendingForcedOpen = false;
    openScaleModal();
  }

  $: hasSelection = selectedPageList.length > 0;
  // Every selected sheet has a scale, so this step has nothing left to do on
  // them. The stepper no longer advances on its own, so the way onward has to
  // be offered rather than taken — see the sibling controls in steps 2 and 3.
  $: allSelectedScaled = hasSelection && selectedPageList.every((p) => isScaled(pages[p.id]));

  // Frames across the selected pages whose clip already carries its own scale —
  // the population the override checkbox acts on, so the count and the write
  // can't disagree (same filter as pageScaleUpdates).
  $: overridableCount = selectedPageList
    .flatMap((p) => indexedLocations[p.id] || [])
    .filter((l) => l.clip?.drawing_scale).length;

  // Neither button is ever disabled for want of a selection — a dead button
  // makes the user hunt for the reason. Clicking answers it instead. Cleared
  // the moment a selection appears, because the warning stops being true, and
  // after a few seconds otherwise so it does not linger. Re-clicking restarts
  // the timer rather than stacking a second one.
  let showSelectWarning = false;
  let selectWarningTimer;

  function warnNoSelection() {
    showSelectWarning = true;
    clearTimeout(selectWarningTimer);
    selectWarningTimer = setTimeout(() => (showSelectWarning = false), 5000);
  }

  $: if (hasSelection && showSelectWarning) {
    clearTimeout(selectWarningTimer);
    showSelectWarning = false;
  }

  onDestroy(() => clearTimeout(selectWarningTimer));

  function handleSetScale() {
    if (!hasSelection) return warnNoSelection();
    openScaleModal();
  }

  function openScaleModal() {
    // Preload the dropdown with what the selection already is, when every selected
    // sheet agrees; otherwise leave it on "None" rather than guessing.
    //
    // N/A is tested first and separately because it is not a scale: an N/A page
    // carries `scale: null` alongside the notApplicable flag, so reading `scale`
    // alone reported it as "None" and the dropdown could never show the state the
    // user had set. A selection that disagrees about N/A falls through to the scale
    // test, which is the same "don't guess" rule.
    if (selectedPageList.length && selectedPageList.every((p) => isNotApplicable(pages[p.id]))) {
      scaleValue = "__na__";
    } else {
      const scales = new Set(selectedPageList.map((p) => pages[p.id]?.scale ?? null));
      scaleValue = scales.size === 1 ? [...scales][0] : null;
    }
    // Checked by default; unchecking is a per-apply choice, never sticky.
    overrideExisting = true;
    scaleModal.open();
  }

  function applyScale() {
    const pageIds = selectedPageList.map((p) => p.id);

    if (scaleValue === "__na__") {
      // N/A means "not a scaled drawing": clear any scale and mark the
      // pages skipped-on-purpose so the canvas flag reads grey N/A and
      // membership (isInFrameX) drops them.
      setPageScale(null);
      setPageFlags(group, doc, pageIds, { notApplicable: true });
    } else {
      setPageScale(scaleValue);
      // Unconditional, "None" included. None means "no scale yet", which is a
      // different state from "not a scaled drawing" — so choosing it has to return
      // the page to NO SCALE. Guarding this on a non-null scale made N/A a one-way
      // door: the page kept reading N/A and stayed out of FrameX whatever the
      // dropdown was changed to.
      setPageFlags(group, doc, pageIds, { notApplicable: false });
    }

    // If the modal was forced open by a blocked placement and a real scale
    // was set, re-arm the frame tool so the user can place the frame right
    // away — no need to re-click the tool button.
    if (forced && scaleValue !== "__na__" && scaleValue !== null) {
      $currentTool = "location-collection";
      dispatch("click-location-tool");
    }
    forced = false;

    scaleModal.close();
  }

  // The canonical scale write (page.scale + drawing_scale seeded onto frames
  // without an override) lives in page-scale-updates.js, shared with the
  // scale-from-dimension dialog. Also used to clear a scale (value: null),
  // which is why it never touches notApplicable.
  function setPageScale(value) {
    const pageIds = selectedPageList.map((p) => p.id);
    group.update(pageScaleUpdates({ doc, grp: $group, indexedLocations, pageIds, value, overrideExisting }));
  }

  function pickDimension() {
    scaleModal.close();
    dispatch("pick-dimension");
  }
</script>

<div class="fx-actions">
  <!-- Setting the scale from the list is the primary path; the secondary arms
       the pick-scale-dim tool for reading the scale off a written dimension.

       The primary's label names the means rather than the scope, and the
       trailing ellipsis says a dialog follows — matching step 3's "Build
       Frames…". The scope is stated inside that dialog ("Setting scale for N
       selected page(s)"), so it is deferred rather than dropped.

       Only the primary needs a selection, and it stays live without one:
       clicking it raises the warning below rather than doing nothing, because
       a dead button states that something is wrong without saying what. The
       dimension tool acts on whichever page is clicked, so the secondary has
       no precondition to warn about. -->
  <button class="fx-primary-button" on:click={handleSetScale}> Set scale from list… </button>
  <button class="fx-secondary-button" on:click={() => dispatch("pick-dimension")}>
    Set scale from dimension
  </button>
  {#if showSelectWarning}
    <div class="fx-warning">No pages are selected. A scale applies only to the pages you select.</div>
  {:else if hasSelection}
    <div class="fx-hint">
      Set from dimension: click both ends of a written dimension on the drawing, then type its value as
      written.
    </div>
  {/if}
  {#if allSelectedScaled}
    <button class="fx-continue" on:click={() => dispatch("continue-to-locate")}>
      Continue to Find Frames →
    </button>
  {/if}
</div>

<Modal
  bind:this={scaleModal}
  closeable
  on:confirm={applyScale}
  on:close={() => {
    forcedNote = "";
    forced = false;
    consumedForcePageId = null;
  }}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Set Scale", type: "confirm", style: "primary" },
  ]}>
  <div slot="title">Set Page Scale</div>
  <div slot="content" class="space-y-4">
    {#if forcedNote}
      <p class="fx-forced-note">{forcedNote}</p>
    {/if}
    <div class="space-y-2">
      <p class="text-sm">
        Setting scale for {selectedPageList.length} selected page{plural(selectedPageList.length)}.
      </p>
      <SelectInput
        border
        label="Drawing Scale"
        options={[
          { label: "None", value: null },
          { label: "N/A — not a scaled drawing", value: "__na__" },
          { label: "Set Custom Scale", value: "custom", unclickable: true },
          ...scaleOptions,
          ...(scaleValue && scaleValue !== "__na__" && !scaleOptions.some((o) => o.value === scaleValue)
            ? [{ label: `Custom Scale: ${scaleValue}`, value: scaleValue }]
            : []),
        ]}
        bind:value={scaleValue}
        on:option-click={() => customScaleModal.open(scaleValue)} />
      <!-- Hidden for None/N/A: clearing never touches frame scales (the write
           enforces this too), so the checkbox would be claiming a choice that
           does not exist there. -->
      {#if overridableCount > 0 && scaleValue !== null && scaleValue !== "__na__"}
        <label class="flex gap-2 items-center text-sm">
          <input type="checkbox" bind:checked={overrideExisting} />
          <span>Apply scale to existing frames</span>
        </label>
      {/if}
    </div>
    <div class="text-sm">
      No visible scale? <button class="btn-text" on:click={pickDimension}>Pick a dimension</button>
    </div>
  </div>
</Modal>
<CustomScaleModal bind:this={customScaleModal} on:setScale={(e) => (scaleValue = e.detail.value)} />

<style lang="scss">
  .fx-forced-note {
    background: var(--fx-amber-bg);
    border: 1px solid var(--fx-amber-border);
    color: var(--fx-amber-text);
    font-size: 12px;
    font-weight: 600;
    padding: 8px 9px;
    border-radius: 6px;
    line-height: 1.4;
  }
</style>
